// src/features/feedback/capture.ts
export const FEEDBACK_WIDGET_ATTR = 'data-feedback-widget';

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function isInsideWidget(node: Node): boolean {
  const el = node instanceof Element ? node : node.parentElement;
  return !!el?.closest(`[${FEEDBACK_WIDGET_ATTR}]`);
}

/** 撮影を待つ上限。超えたら失敗として扱う。 */
const CAPTURE_TIMEOUT_MS = 10000;

/** 外部画像の取得待ちと iframe の中身は、撮影を遅くするだけなので写さない。 */
function isSkippedForSpeed(node: Node): boolean {
  return node instanceof HTMLImageElement || node instanceof HTMLIFrameElement;
}

/** ページの DOM を画像にする。フィードバックのパネルとボタンは写さない。 */
export async function captureDomScreenshot(): Promise<Blob> {
  const { toBlob } = await import('html-to-image');
  // 呼び出し側でパネルを非表示にした結果が描画に反映されるまで待つ
  await nextFrame();
  await nextFrame();

  let watchdog: number | undefined;
  let blob: Blob | null;
  try {
    blob = await Promise.race([
      toBlob(document.body, {
        pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
        // 社内確認用なので完全さより速さを優先する。
        // Web フォントの埋め込み（BIZ UDPGothic など数百ファイル）と画像の取得は
        // 計測で 20 秒以上かかる／CORS で失敗するため、どちらも撮影対象から外す。
        skipFonts: true,
        filter: (node) => !isInsideWidget(node) && !isSkippedForSpeed(node),
      }),
      new Promise<never>((_, reject) => {
        watchdog = window.setTimeout(() => reject(new Error('capture timed out')), CAPTURE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    window.clearTimeout(watchdog);
  }
  if (!blob) throw new Error('capture returned empty');
  return blob;
}
